import { useRef, useEffect, useLayoutEffect, useCallback, useState, cloneElement, isValidElement, Children } from 'react';
import { createPortal } from 'react-dom';
import cx from 'classnames';
import './Popover.css';

/**
 * QHS Popover v2 (base)
 * ---------------------
 * The container used by ActionMenu / InlinePicker / FormPopover.
 * Handles anchor positioning (below trigger by default), click-outside,
 * Escape to close, and body scroll-lock on mobile.
 *
 *   const [open, setOpen] = useState(false);
 *   <Popover
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     trigger={<button ref={...} onClick={() => setOpen(v => !v)}>Open</button>}
 *     placement="bottom-start"
 *   >
 *     <div>content</div>
 *   </Popover>
 */
const Popover = ({
  open,
  onClose,
  trigger,
  placement = 'bottom-start',
  offset = 6,
  matchTriggerWidth = false,
  className,
  children,
}) => {
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const [coords, setCoords] = useState(null);

  // Clone trigger to inject ref (if it's a React element)
  const renderedTrigger = isValidElement(trigger)
    ? cloneElement(trigger, {
        ref: node => {
          triggerRef.current = node;
          const origRef = trigger.ref;
          if (typeof origRef === 'function') origRef(node);
          else if (origRef) origRef.current = node;
        },
      })
    : trigger;

  const positionPopover = useCallback(() => {
    if (!triggerRef.current || !popoverRef.current) return;
    const t = triggerRef.current.getBoundingClientRect();
    const p = popoverRef.current;
    // Fall back to matchTriggerWidth's width during first measure: the
    // popover hasn't painted yet so offsetWidth may be 0 until the
    // browser lays it out. Use trigger width + a generous height so
    // the initial render positions below the trigger, then we re-
    // measure on the next frame for pixel-perfect alignment.
    const pw = p.offsetWidth || (matchTriggerWidth ? t.width : 200);
    const ph = p.offsetHeight || 200;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top = 0;
    let left = 0;

    switch (placement) {
      case 'bottom-end':    top = t.bottom + offset; left = t.right - pw; break;
      case 'bottom-center': top = t.bottom + offset; left = t.left + t.width / 2 - pw / 2; break;
      case 'top-start':     top = t.top - ph - offset; left = t.left; break;
      case 'top-end':       top = t.top - ph - offset; left = t.right - pw; break;
      case 'bottom-start':
      default:              top = t.bottom + offset; left = t.left; break;
    }

    // Collision avoidance
    if (left + pw > vw - 8) left = vw - pw - 8;
    if (left < 8) left = 8;
    if (top + ph > vh - 8) top = t.top - ph - offset;
    if (top < 8) top = 8;

    setCoords({
      top,
      left,
      ...(matchTriggerWidth ? { width: t.width } : {}),
    });
  }, [placement, offset, matchTriggerWidth]);

  useLayoutEffect(() => {
    if (open) positionPopover();
  }, [open, positionPopover, children]);

  // Clear coords on close (separate effect so adding `coords` to the
  // layout-effect deps doesn't loop: positionPopover sets coords →
  // deps change → effect re-runs → measures again → sets coords → …
  useEffect(() => {
    if (!open) setCoords(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onResize = () => positionPopover();
    const onScroll = () => positionPopover();
    const onKey = e => {
      if (e.key === 'Escape') onClose?.();
    };
    const onClick = e => {
      if (popoverRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      // Nested popover: if the click landed inside ANY popover
      // surface (a child popover portaled to <body>, e.g. an
      // InlinePicker opened from this popover's content), the
      // child owns its own dismissal flow and we must not collapse
      // the parent — otherwise the child closes before its onChange
      // handler can run, and the user's selection is lost.
      if (typeof e.target.closest === 'function' && e.target.closest('.qhs-pop')) return;
      onClose?.();
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open, onClose, positionPopover]);

  // Always render the portal while `open` so `popoverRef.current`
  // becomes available for `positionPopover()` to measure. Until
  // coords are computed, keep the popover visually hidden to prevent
  // a flash of content at (0, 0).
  return (
    <>
      {renderedTrigger}
      {open && createPortal(
        <div
          ref={popoverRef}
          className={cx('qhs-pop', className)}
          style={{
            top: coords?.top ?? 0,
            left: coords?.left ?? 0,
            ...(coords?.width ? { width: coords.width } : {}),
            visibility: coords ? 'visible' : 'hidden',
          }}
          role="dialog"
        >
          {children}
        </div>,
        document.body
      )}
    </>
  );
};

export default Popover;
